import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";

/**
 * Electronic tom drum instrument with pitched synthesis.
 *
 * Creates electronic tom sounds using dual-layer synthesis:
 * - Tone layer: Dual oscillators (sine + triangle) with pitch envelope
 * - Noise layer: Filtered white noise for attack transient
 *
 * The pitch envelope creates the characteristic tom "boom" by sweeping
 * from high to low frequencies. Each tom instance is tuned to a different
 * base pitch (low, mid, high) for a full tom kit.
 */
export class ElectroTom extends Instrument {
  // Core tom parameters
  private basePitch: number;
  private pitchDecayTime: number = 0.15;
  private pitchEnvelopeAmount: number = 12; // Semitones to sweep down

  // Tone layer parameters
  private toneAttack: number = 0.002;
  private toneDecay: number = 0.3;
  private toneMix: number = 0.7;

  // Noise layer parameters
  private noiseAttack: number = 0.001;
  private noiseDecay: number = 0.02;
  private noiseMix: number = 0.3;
  private noiseFilterFreq: number = 4000;

  constructor(audioContext: AudioContext, output: GainNode, basePitch: number) {
    super(audioContext, output);
    this.basePitch = basePitch;
    this.initializeParameters();
  }

  /**
   * Play an electronic tom hit with pitch sweep and noise transient.
   */
  playNote(note: MusicalNote, startTime: number, duration: number): void {
    const velocity = note.velocity ?? 1.0;

    // Create tone layer with dual oscillators
    const osc1 = this.audioContext.createOscillator();
    const osc2 = this.audioContext.createOscillator();
    const toneGain = this.audioContext.createGain();

    // Configure oscillators
    osc1.type = "sine";
    osc2.type = "triangle";

    // Set base frequencies (osc2 slightly detuned for richness)
    const baseFreq = this.midiToFrequency(this.basePitch);
    const startFreq = baseFreq * Math.pow(2, this.pitchEnvelopeAmount / 12);

    osc1.frequency.setValueAtTime(startFreq, startTime);
    osc1.frequency.exponentialRampToValueAtTime(
      baseFreq,
      startTime + this.pitchDecayTime,
    );

    osc2.frequency.setValueAtTime(startFreq * 1.01, startTime);
    osc2.frequency.exponentialRampToValueAtTime(
      baseFreq * 1.01,
      startTime + this.pitchDecayTime,
    );

    // Tone envelope
    toneGain.gain.setValueAtTime(0, startTime);
    toneGain.gain.linearRampToValueAtTime(
      this.toneMix * velocity,
      startTime + this.toneAttack,
    );
    toneGain.gain.exponentialRampToValueAtTime(
      0.001,
      startTime + this.toneAttack + this.toneDecay,
    );

    // Create noise layer for attack transient
    const noiseBuffer = this.createNoiseBuffer(0.1);
    const noiseSource = this.audioContext.createBufferSource();
    const noiseFilter = this.audioContext.createBiquadFilter();
    const noiseGain = this.audioContext.createGain();

    noiseSource.buffer = noiseBuffer;
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.value = this.noiseFilterFreq;
    noiseFilter.Q.value = 2;

    // Noise envelope (very short)
    noiseGain.gain.setValueAtTime(0, startTime);
    noiseGain.gain.linearRampToValueAtTime(
      this.noiseMix * velocity,
      startTime + this.noiseAttack,
    );
    noiseGain.gain.exponentialRampToValueAtTime(
      0.001,
      startTime + this.noiseAttack + this.noiseDecay,
    );

    // Connect audio graph
    osc1.connect(toneGain);
    osc2.connect(toneGain);
    toneGain.connect(this.gain);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.gain);

    // Schedule playback
    osc1.start(startTime);
    osc2.start(startTime);
    noiseSource.start(startTime);

    const stopTime =
      startTime +
      Math.max(
        this.toneAttack + this.toneDecay,
        this.noiseAttack + this.noiseDecay,
      ) +
      0.1;

    osc1.stop(stopTime);
    osc2.stop(stopTime);
    noiseSource.stop(stopTime);

    // Clean up
    osc1.onended = () => {
      osc1.disconnect();
      osc2.disconnect();
      toneGain.disconnect();
      noiseSource.disconnect();
      noiseFilter.disconnect();
      noiseGain.disconnect();
    };
  }

  /**
   * Create a white noise buffer for transient layer.
   */
  private createNoiseBuffer(duration: number): AudioBuffer {
    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const channel = buffer.getChannelData(0);

    for (let i = 0; i < length; i++) {
      channel[i] = Math.random() * 2 - 1;
    }

    return buffer;
  }

  /**
   * Convert MIDI note number to frequency.
   */
  private midiToFrequency(midiNote: number): number {
    return 440 * Math.pow(2, (midiNote - 69) / 12);
  }

  /**
   * Get valid MIDI notes for this tom.
   */
  getValidNotes(): number[] {
    // Return the MIDI note number based on base pitch
    // This maps to standard tom assignments:
    // 41 = Low Floor Tom (F2)
    // 43 = High Floor Tom (G2)
    // 45 = Low Tom (A2)
    return [this.basePitch];
  }

  /**
   * Get display name including tom type.
   */
  getDisplayName(): string {
    switch (this.basePitch) {
      case 41:
        return "Low Tom";
      case 43:
        return "Mid Tom";
      case 45:
        return "High Tom";
      default:
        return `Tom (${this.basePitch})`;
    }
  }

  /**
   * Initialize tom-specific parameters.
   */
  private initializeParameters(): void {
    // Pitch parameters
    this.addParameter(
      () => this.pitchEnvelopeAmount,
      (value) => {
        this.pitchEnvelopeAmount = value;
      },
      {
        key: "pitchEnvAmount",
        label: "Pitch Sweep",
        min: 0,
        max: 24,
        defaultValue: 12,
        step: 1,
        unit: "semitones",
        category: "Pitch",
      },
    );

    this.addParameter(
      () => this.pitchDecayTime,
      (value) => {
        this.pitchDecayTime = value;
      },
      {
        key: "pitchDecay",
        label: "Pitch Decay",
        min: 0.05,
        max: 0.5,
        defaultValue: 0.15,
        step: 0.01,
        unit: "s",
        category: "Pitch",
      },
    );

    // Tone layer parameters
    this.addParameter(
      () => this.toneMix,
      (value) => {
        this.toneMix = value;
      },
      {
        key: "toneMix",
        label: "Tone Mix",
        min: 0,
        max: 1,
        defaultValue: 0.7,
        step: 0.05,
        category: "Tone",
      },
    );

    this.addParameter(
      () => this.toneDecay,
      (value) => {
        this.toneDecay = value;
      },
      {
        key: "toneDecay",
        label: "Tone Decay",
        min: 0.1,
        max: 1.0,
        defaultValue: 0.3,
        step: 0.05,
        unit: "s",
        category: "Tone",
      },
    );

    // Noise layer parameters
    this.addParameter(
      () => this.noiseMix,
      (value) => {
        this.noiseMix = value;
      },
      {
        key: "noiseMix",
        label: "Noise Mix",
        min: 0,
        max: 1,
        defaultValue: 0.3,
        step: 0.05,
        category: "Noise",
      },
    );

    this.addParameter(
      () => this.noiseFilterFreq,
      (value) => {
        this.noiseFilterFreq = value;
      },
      {
        key: "noiseFilter",
        label: "Noise Filter",
        min: 1000,
        max: 8000,
        defaultValue: 4000,
        step: 100,
        unit: "Hz",
        category: "Noise",
      },
    );
  }
}
